import { Box } from '@mui/material';
import { DataGridProProps, GridColDef, GridColumnGroupingModel } from '@mui/x-data-grid-pro';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useCallback, useState } from 'react';
import { useParams } from 'react-router';
import { StyleObj } from '../../../../@types';
import { PaginatedData, WalletCredit } from '../../../../@types/api';
import { PAYMENT_GATEWAY } from '../../../../constants/enums';
import { QUERY_KEYS } from '../../../../constants';
import { AdditionalQueryParams } from '../../../../hooks/usePagination';
import { useMarketplace } from '../../../../hooks/useMarketplace';
import { createColumn, floorToFixed } from '../../../../utils';
import { getData } from '../../../../utils/api';
import DateRangeSelect, { DayjsTimestamp } from '../../../molecules/DateRangeSelect';
import TableTemplate from '../../../templates/TableTemplate';

const styles: StyleObj = {
  container: {
    position: 'relative',
    height: 'calc(100% - 200px)',
  },
  table: {
    border: 'none',
    '--unstable_DataGrid-radius': '0',
    '& .MuiDataGrid-withBorderColor': {
      border: 'none',
    },
    '& .MuiDataGrid-columnHeader': {
      border: '1px solid rgba(0, 83, 55, 0.20)',
      backgroundColor: 'background.lightGreen',
      '&:first-of-type': {
        borderRadius: 0,
      },
      '&:not(:first-of-type)': {
        borderLeft: 'none',
      },
      '&:last-of-type': {
        borderRadius: 0,
        width: '100% !important',
        maxWidth: 'unset !important',
      },
    },
    '& .MuiDataGrid-columnHeadersInner': {
      width: '100%',
      '> div': {
        width: '100%',
      },
    },
    '& .MuiDataGrid-columnHeaders': {
      borderRadius: 0,
    },
    '& .MuiDataGrid-columnHeaderTitle': {
      fontWeight: 700,
      color: 'grey.700',
    },
    '& .MuiDataGrid-row:nth-of-type(even)': {
      backgroundColor: 'background.light',
      '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.04)',
      },
    },
    '& .MuiDataGrid-columnHeader:focus-within': {
      outline: 'none',
    },
    '& .MuiDataGrid-footerContainer': {
      display: 'none',
    },
    '& .MuiDataGrid-columnHeaders:hover .MuiDataGrid-columnSeparator': {
      visibility: 'hidden',
    },
    '& .customGroupHeader': {
      backgroundColor: 'primary.main',
      '&:first-of-type': {
        borderTopLeftRadius: 8,
      },
      '&:last-of-type': {
        borderTopRightRadius: 8,
      },
      '& .MuiDataGrid-columnHeaderTitleContainer': {
        justifyContent: 'center',
      },
      '& .MuiDataGrid-columnHeaderTitle': {
        fontWeight: 700,
        color: 'white',
      },
    },
  },
};

const DATA_TYPES = {
  amountSum: 'Sum',
  countNum: 'Number',
};

const formatNumberValue = (value: number, dataType: keyof typeof DATA_TYPES) => {
  return dataType === 'amountSum' ? floorToFixed(value, 2) : value;
};

const createNumberColumn = (field: string, headerName: string, rowAccessor: (row: WalletCredit) => number) => {
  return createColumn(field, headerName, {
    sortable: false,
    valueGetter: (value, row, column, apiRef) => formatNumberValue(rowAccessor(row), row.dataType),
  });
};

const columns: GridColDef[] = [
  createColumn('dataType', '', {
    sortable: false,
    valueGetter: (value, row, column, apiRef) => DATA_TYPES[row.dataType as keyof typeof DATA_TYPES],
  }),
  createNumberColumn('deposit', 'Deposit', (row) => row.in.deposit),
  createNumberColumn('bonus', 'Bonus', (row) => row.in.bonus),
  createNumberColumn('otherIn', 'Other', (row) => row.in.other),
  createNumberColumn('totalIn', 'Total', (row) => row.in.total),
  createNumberColumn('withdrawal', 'Withdrawal', (row) => row.out.withdrawal),
  createNumberColumn('otherOut', 'Other', (row) => row.out.other),
  createNumberColumn('totalOut', 'Total', (row) => row.out.total),
];

const columnGroupingModel: GridColumnGroupingModel = [
  {
    groupId: 'Total',
    description: 'Total',
    headerClassName: 'customGroupHeader',
    children: [{ field: 'dataType' }],
  },
  {
    groupId: 'In',
    description: 'In',
    headerClassName: 'customGroupHeader',
    children: [{ field: 'deposit' }, { field: 'bonus' }, { field: 'otherIn' }, { field: 'totalIn' }],
  },
  {
    groupId: 'Out',
    description: 'Out',
    headerClassName: 'customGroupHeader',
    children: [{ field: 'otherOut' }, { field: 'totalOut' }, { field: 'withdrawal' }],
  },
];

type TotalCreditQueryParams = {
  fromTimestamp: number;
  toTimestamp: number;
  paymentMethod?: string;
};

const TotalCreditPage = () => {
  const [date, setDate] = useState<DayjsTimestamp>({
    fromTimestamp: dayjs().startOf('day'),
    toTimestamp: dayjs().endOf('day'),
  });
  const [queryParams, setQueryParams] = useState<TotalCreditQueryParams>({
    fromTimestamp: dayjs().startOf('day').valueOf(),
    toTimestamp: dayjs().endOf('day').valueOf(),
  });
  const { config } = useMarketplace();
  const hasCryptoPaymentGateway = config?.marketplaceConfig?.paymentGateway === PAYMENT_GATEWAY.CRYPTO;

  const handleDateChange = useCallback((newDate: DayjsTimestamp) => {
    setDate(newDate);
    setQueryParams((prevValue) => ({
      ...prevValue,
      fromTimestamp: newDate.fromTimestamp.valueOf(),
      toTimestamp: newDate.toTimestamp.valueOf(),
    }));
  }, []);

  const { userId } = useParams();

  const changeQuery = useCallback((query: AdditionalQueryParams) => {
    setQueryParams((prevValue) => {
      const nextValue: TotalCreditQueryParams = {
        ...prevValue,
        ...query,
      };

      if (!nextValue.paymentMethod) {
        delete nextValue.paymentMethod;
      }

      return nextValue;
    });
  }, []);

  const {
    data: balanceData,
    isPending,
    isFetching,
  } = useQuery<PaginatedData<WalletCredit>>({
    queryKey: [QUERY_KEYS.credits, userId, queryParams],

    queryFn: () =>
      getData(
        'wallets/total-credits',
        {
          userId,
          ...queryParams,
        },
        'wallet'
      ),

    enabled: !!userId,
  });

  return (
    <Box sx={styles.container}>
      <TableTemplate
        rows={balanceData?.items || []}
        columns={columns}
        rowCount={balanceData?.count || 0}
        loading={isPending || isFetching}
        getRowId={(row: WalletCredit) => row.dataType}
        sx={styles.table as DataGridProProps['sx']}
        showFilter={hasCryptoPaymentGateway}
        changeQuery={changeQuery}
        columnGroupingModel={columnGroupingModel}
        customTableHeaderContent={<DateRangeSelect date={date} onDateChange={handleDateChange} />}
      />
    </Box>
  );
};

export default TotalCreditPage;
